/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { type AcesDealsVehicleAttributes } from '../interface';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { type VehicleSummary } from '@/types';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';

type AdditionalAttributes = Record<string, string | undefined>;

/**
 * React hook to get ACES vehicle attributes for deals
 * @returns AcesDealsVehicleAttributes object or null if ACES is not enabled or no preferred vehicle
 */
export function useAcesDealsVehicleAttributes(
  additionalAttributes: AdditionalAttributes = {},
): AcesDealsVehicleAttributes | null {
  const preferredVehicle = usePreferredVehicle();
  const { acesEnabled } = useAcesFlags();

  if (!acesEnabled || !preferredVehicle) {
    return null;
  }

  return getAcesDealsVehicleAttributes(preferredVehicle, acesEnabled, additionalAttributes);
}

/**
 * Server-side helper function to get ACES vehicle attributes for deals
 * @param preferredVehicle - The preferred vehicle data
 * @param acesEnabled - Flag indicating if ACES is enabled
 * @returns AcesDealsVehicleAttributes object or null if ACES is not enabled or no preferred vehicle
 */
export function getAcesDealsVehicleAttributes(
  preferredVehicle: VehicleSummary | null | undefined,
  acesEnabled: boolean,
  additionalAttributes: AdditionalAttributes = {},
): AcesDealsVehicleAttributes | null {
  if (!acesEnabled || !preferredVehicle) {
    return null;
  }

  const vehicleAttributes: AcesDealsVehicleAttributes = {
    makeId: String(preferredVehicle.makeId ?? ''),
    modelId: String(preferredVehicle.modelId ?? ''),
    vehicleQuestions: extractVehicleQuestions(
      preferredVehicle.acesVehicleAttributes?.vehicleQuestionsIds,
      true,
    ) as string,
    vehicleTypeId: String(preferredVehicle.vehicleTypeId ?? ''),
    year: String(preferredVehicle.year),
    ...additionalAttributes,
  };

  // Remove falsy values from the object
  Object.keys(vehicleAttributes).forEach((key) => {
    if (!vehicleAttributes[key as keyof AcesDealsVehicleAttributes]) {
      delete vehicleAttributes[key as keyof AcesDealsVehicleAttributes];
    }
  });

  return vehicleAttributes;
}
